import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search,
  Sparkles,
  TrendingUp,
  Clock,
  Music,
  Upload,
  ChevronRight,
  Play,
  Download,
  Heart,
  Monitor,
  Zap
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { SequenceCard } from '@/components/sequences/SequenceCard';
import { SequencerCard } from '@/components/sequences/SequencerCard';
import { GenreCard as ReusableGenreCard } from '@/components/sequences/GenreCard';
import { Sequence } from '@/types/sequence';
import { useAllSequencers } from '@/hooks/useAllSequencers';
import { cn } from '@/lib/utils';

interface CategoryCardProps {
  title: string;
  description: string;
  count: number;
  href: string;
  gradient: string;
  icon: React.ReactNode;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ title, description, count, href, gradient, icon }) => (
  <Link to={href}>
    <Card className={cn(
      "group overflow-hidden border-2 hover:border-primary transition-all duration-300 h-full",
      "hover:shadow-lg hover:-translate-y-1"
    )}>
      <div className={cn("h-32 relative overflow-hidden", gradient)}>
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent" />
        <div className="absolute top-4 right-4 text-white/90">
          {icon}
        </div>
        <div className="absolute bottom-4 left-4">
          <Badge variant="secondary" className="bg-white/90 text-foreground">
            {count} sequences
          </Badge>
        </div>
      </div>
      <CardHeader>
        <CardTitle className="group-hover:text-primary transition-colors">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardFooter>
        <Button variant="ghost" size="sm" className="w-full group-hover:bg-secondary">
          Browse All <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  </Link>
);

const SequencesCatalog: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [trendingSequences, setTrendingSequences] = useState<Sequence[]>([]);
  const [latestSequences, setLatestSequences] = useState<Sequence[]>([]);
  const [genres, setGenres] = useState<Array<{ name: string; slug: string; count: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    free: 0,
    paid: 0,
    creators: 0
  });

  // Fetch featured sequencers for carousel
  const { sequencers: featuredSequencers, isLoading: featuredLoading } = useAllSequencers({
    filterFeatured: true,
    sortBy: 'sequences',
    limit: 8,
  });

  useEffect(() => {
    loadCatalogData();
  }, []);

  const loadCatalogData = async () => {
    try {
      setIsLoading(true);

      // Load stats
      const { count: totalCount } = await supabase
        .from('sequences')
        .select('*', { count: 'exact', head: true });

      const { count: freeCount } = await supabase
        .from('sequences')
        .select('*', { count: 'exact', head: true })
        .eq('is_free', true);

      const { data: creatorsData } = await supabase
        .from('sequences')
        .select('user_id')
        .not('user_id', 'is', null);

      const uniqueCreators = new Set(creatorsData?.map(s => s.user_id) || []).size;

      setStats({
        total: totalCount || 0,
        free: freeCount || 0,
        paid: (totalCount || 0) - (freeCount || 0),
        creators: uniqueCreators
      });

      // Load trending sequences (by downloads) - Two-step fetch to avoid cross-schema FK joins
      const { data: trendingData } = await supabase
        .from('sequences')
        .select('*, artists(name)')
        .order('download_count', { ascending: false })
        .limit(12);

      if (trendingData && trendingData.length > 0) {
        // Step 2: Get unique user IDs
        const userIds = [...new Set(trendingData.map((s: any) => s.user_id).filter(Boolean))];

        // Step 3: Fetch user profiles
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, display_name')
          .in('id', userIds);

        // Step 4: Create profile map and merge data
        const profileMap = Object.fromEntries(profiles?.map((p: any) => [p.id, p]) || []);
        const enrichedTrendingData = trendingData.map((seq: any) => ({
          ...seq,
          user_profiles: profileMap[seq.user_id]
        }));

        // Step 5: Format data for display
        const formattedTrending = enrichedTrendingData.map((seq: any) => ({
          id: seq.id,
          name: seq.name,
          title: seq.title || seq.name,
          displayName: seq.title || seq.name,
          creatorName: seq.user_profiles?.display_name || 'Unknown',
          artistName: seq.artists?.name || seq.name?.split(' - ')[1] || '',
          image: seq.image_url || seq.album_cover_url,
          price: parseFloat(seq.price || '0'),
          isFree: seq.is_free,
          software: seq.software_type,
          holiday: seq.holiday_type,
          downloadCount: (seq.download_count || 0) + (seq.display_downloads || 0),
          upvoteCount: seq.upvote_count || 0,
          averageRating: seq.average_rating || 0,
          videoUrl: seq.video_url,
          spotifyPreviewUrl: seq.spotify_preview_url,
        }));
        setTrendingSequences(formattedTrending);
      }

      // Load latest sequences - Two-step fetch to avoid cross-schema FK joins
      const { data: latestData } = await supabase
        .from('sequences')
        .select('*, artists(name)')
        .order('created_at', { ascending: false })
        .limit(8);

      if (latestData && latestData.length > 0) {
        // Step 2: Get unique user IDs
        const latestUserIds = [...new Set(latestData.map((s: any) => s.user_id).filter(Boolean))];

        // Step 3: Fetch user profiles
        const { data: latestProfiles } = await supabase
          .from('user_profiles')
          .select('id, display_name')
          .in('id', latestUserIds);

        // Step 4: Create profile map and merge data
        const latestProfileMap = Object.fromEntries(latestProfiles?.map((p: any) => [p.id, p]) || []);
        const enrichedLatestData = latestData.map((seq: any) => ({
          ...seq,
          user_profiles: latestProfileMap[seq.user_id]
        }));

        // Step 5: Format data for display
        const formattedLatest = enrichedLatestData.map((seq: any) => ({
          id: seq.id,
          name: seq.name,
          title: seq.title || seq.name,
          displayName: seq.title || seq.name,
          creatorName: seq.user_profiles?.display_name || 'Unknown',
          artistName: seq.artists?.name || seq.name?.split(' - ')[1] || '',
          image: seq.image_url || seq.album_cover_url,
          price: parseFloat(seq.price || '0'),
          isFree: seq.is_free,
          software: seq.software_type,
          holiday: seq.holiday_type,
          downloadCount: (seq.download_count || 0) + (seq.display_downloads || 0),
          upvoteCount: seq.upvote_count || 0,
          averageRating: seq.average_rating || 0,
          videoUrl: seq.video_url,
          spotifyPreviewUrl: seq.spotify_preview_url,
        }));
        setLatestSequences(formattedLatest);
      }

      // Load top genres
      const { data: genresData } = await supabase
        .from('genres')
        .select('name, slug, sequence_count')
        .gt('sequence_count', 0)
        .eq('is_active', true)
        .order('sequence_count', { ascending: false })
        .limit(16);

      if (genresData) {
        setGenres(genresData.map(g => ({
          name: g.name,
          slug: g.slug || g.name.toLowerCase().replace(/\s+/g, '-'),
          count: g.sequence_count || 0
        })));
      }

    } catch (error) {
      console.error('Error loading catalog data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/sequences/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/sequences/search');
    }
  };

  return (
    <>
      <Helmet>
        <title>Light Display Sequences - Browse, Download & Share | LightsHunt</title>
        <meta
          name="description"
          content={`Discover ${stats.total}+ holiday light display sequences. Download free and premium sequences for xLights, Light-O-Rama, and more. Browse by genre, holiday, or software.`}
        />
        <meta name="keywords" content="light sequences, Christmas sequences, xLights, Light-O-Rama, holiday lights, sequence download, light show sequences" />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />

        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 pt-24 pb-16 px-4">
            <div className="container max-w-6xl mx-auto">
              <div className="text-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                  <Sparkles className="h-4 w-4" />
                  Sequence Marketplace
                </div>

                <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                  Discover Amazing
                  <span className="block text-primary mt-2">Light Display Sequences</span>
                </h1>

                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Browse {stats.total.toLocaleString()}+ sequences from {stats.creators} creators.
                  {stats.free > 0 && ` ${Math.round((stats.free / stats.total) * 100)}% are free to download.`}
                </p>

                {/* Search Bar */}
                <form onSubmit={handleSearch} className="max-w-2xl mx-auto mt-8">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search songs, artists, genres..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-12 pr-4 py-6 text-lg rounded-full border-2 focus:border-primary"
                    />
                  </div>
                </form>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
                  <Card>
                    <CardContent className="p-6 text-center">
                      <div className="text-3xl font-bold text-primary">{stats.total.toLocaleString()}</div>
                      <div className="text-sm text-muted-foreground mt-1">Total Sequences</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6 text-center">
                      <div className="text-3xl font-bold text-green-600">{stats.free.toLocaleString()}</div>
                      <div className="text-sm text-muted-foreground mt-1">Free Sequences</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-6 text-center">
                      <div className="text-3xl font-bold text-blue-600">{stats.paid.toLocaleString()}</div>
                      <div className="text-sm text-muted-foreground mt-1">Premium</div>
                    </CardContent>
                  </Card>
                  <Link to="/sequencers">
                    <Card className="cursor-pointer hover:border-primary hover:shadow-md transition-all duration-200">
                      <CardContent className="p-6 text-center">
                        <div className="text-3xl font-bold text-purple-600">{stats.creators}</div>
                        <div className="text-sm text-muted-foreground mt-1">Creators</div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4 justify-center mt-8">
                  <Button size="lg" onClick={() => navigate('/sequences/search')}>
                    Browse All Sequences
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate('/upload-sequence')}>
                    <Upload className="mr-2 h-5 w-5" />
                    Share Your Sequence
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Featured Categories */}
          <section className="py-16 px-4 bg-muted/30">
            <div className="container max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold">Browse by Holiday</h2>
                  <p className="text-muted-foreground mt-2">Explore sequences organized by holiday type</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <CategoryCard
                  title="Christmas"
                  description="The largest collection of Christmas light sequences"
                  count={1106}
                  href="/sequences/holiday/Christmas"
                  gradient="bg-gradient-to-br from-red-500 to-green-600"
                  icon={<Sparkles className="h-8 w-8" />}
                />
                <CategoryCard
                  title="Halloween"
                  description="Spooky and spectacular Halloween sequences"
                  count={17}
                  href="/sequences/holiday/Halloween"
                  gradient="bg-gradient-to-br from-orange-500 to-purple-600"
                  icon={<Play className="h-8 w-8" />}
                />
                <CategoryCard
                  title="All Holidays"
                  description="Browse sequences for every holiday"
                  count={stats.total}
                  href="/sequences/search"
                  gradient="bg-gradient-to-br from-blue-500 to-purple-600"
                  icon={<TrendingUp className="h-8 w-8" />}
                />
              </div>
            </div>
          </section>

          {/* Browse by Software */}
          <section className="py-16 px-4">
            <div className="container max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold">Browse by Software</h2>
                  <p className="text-muted-foreground mt-2">Find sequences compatible with your display software</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <CategoryCard
                  title="xLights"
                  description="Professional open-source sequencing software"
                  count={1135}
                  href="/sequences/xLights"
                  gradient="bg-gradient-to-br from-blue-500 to-cyan-600"
                  icon={<Monitor className="h-8 w-8" />}
                />
                <CategoryCard
                  title="Light-O-Rama"
                  description="Industry-standard commercial sequencing platform"
                  count={8}
                  href="/sequences/Light-O-Rama"
                  gradient="bg-gradient-to-br from-purple-500 to-pink-600"
                  icon={<Zap className="h-8 w-8" />}
                />
              </div>
            </div>
          </section>

          {/* Featured Sequencers */}
          {featuredSequencers.length > 0 && (
            <section className="py-16 px-4 bg-muted/30">
              <div className="container max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-3xl font-bold flex items-center gap-2">
                      <Sparkles className="h-8 w-8 text-primary" />
                      Featured Creators
                    </h2>
                    <p className="text-muted-foreground mt-2">
                      Hand-picked creators showcasing exceptional quality and creativity
                    </p>
                  </div>
                  <Link to="/sequencers">
                    <Button variant="outline">
                      View All Creators <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>

                {featuredLoading ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Loading featured creators...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {featuredSequencers.map((sequencer) => (
                      <SequencerCard
                        key={sequencer.id}
                        userId={sequencer.userId}
                        displayName={sequencer.displayName}
                        username={sequencer.username}
                        avatarUrl={sequencer.avatarUrl}
                        bio={sequencer.bio}
                        socialLinks={sequencer.socialLinks}
                        sequenceCount={sequencer.sequenceCount}
                        totalDownloads={sequencer.totalDownloads}
                        averageRating={sequencer.averageRating}
                        freeSequenceCount={sequencer.freeSequenceCount}
                        paidSequenceCount={sequencer.paidSequenceCount}
                        featured={sequencer.featured}
                        verified={sequencer.verified}
                        createdAt={sequencer.createdAt}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Trending Sequences */}
          <section className="py-16 px-4">
            <div className="container max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold flex items-center gap-2">
                    <TrendingUp className="h-8 w-8 text-primary" />
                    Trending Sequences
                  </h2>
                  <p className="text-muted-foreground mt-2">Most downloaded sequences this month</p>
                </div>
                <Link to="/sequences/search?sort=download_count:desc">
                  <Button variant="outline">
                    View All <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>

              {isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Card key={i}>
                      <Skeleton className="aspect-video w-full" />
                      <CardContent className="p-4">
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-2/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {trendingSequences.slice(0, 8).map((sequence) => (
                    <SequenceCard key={sequence.id} sequence={sequence} />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Browse by Genre */}
          <section className="py-16 px-4 bg-muted/30">
            <div className="container max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold flex items-center gap-2">
                    <Music className="h-8 w-8 text-primary" />
                    Browse by Genre
                  </h2>
                  <p className="text-muted-foreground mt-2">Find sequences in your favorite music genre</p>
                </div>
                <Link to="/sequences/genres">
                  <Button variant="outline">
                    View All Genres <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>

              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <Card key={i}>
                      <Skeleton className="h-20" />
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {genres.slice(0, 12).map((genre) => (
                    <ReusableGenreCard key={genre.slug} name={genre.name} slug={genre.slug} count={genre.count} />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Latest Uploads */}
          <section className="py-16 px-4">
            <div className="container max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold flex items-center gap-2">
                    <Clock className="h-8 w-8 text-primary" />
                    Latest Uploads
                  </h2>
                  <p className="text-muted-foreground mt-2">Recently added sequences</p>
                </div>
                <Link to="/sequences/search?sort=created_at:desc">
                  <Button variant="outline">
                    View All <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>

              {isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Card key={i}>
                      <Skeleton className="aspect-video w-full" />
                      <CardContent className="p-4">
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-2/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {latestSequences.map((sequence) => (
                    <SequenceCard key={sequence.id} sequence={sequence} />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Quick Filters */}
          <section className="py-16 px-4 bg-muted/30">
            <div className="container max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center">Quick Filters</h2>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/sequences/search?free=true">
                  <Badge variant="secondary" className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                    <Download className="mr-2 h-4 w-4" />
                    Free Only
                  </Badge>
                </Link>
                <Link to="/sequences/search?software=xLights">
                  <Badge variant="secondary" className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                    xLights
                  </Badge>
                </Link>
                <Link to="/sequences/holiday/Christmas">
                  <Badge variant="secondary" className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                    Christmas
                  </Badge>
                </Link>
                <Link to="/sequences/holiday/Halloween">
                  <Badge variant="secondary" className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                    Halloween
                  </Badge>
                </Link>
                <Link to="/sequences/search?featured=true">
                  <Badge variant="secondary" className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                    <Heart className="mr-2 h-4 w-4" />
                    Featured
                  </Badge>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default SequencesCatalog;
